import React, { useState } from "react";
import Editor, { Toolbar, BtnBold, BtnItalic, BtnUnderline, BtnLink } from "react-simple-wysiwyg";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CodeIcon from "@mui/icons-material/Code";
import EditIcon from "@mui/icons-material/Edit";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";

function SettingsSection({ icon, title, subtitle, children }) {
  return (
    <section className="cinbox-settings-section">
      <div className="cinbox-settings-section-header">
        <div className="cinbox-settings-section-icon">{icon}</div>
        <div>
          <p className="cinbox-settings-row-title">{title}</p>
          {subtitle && <p className="cinbox-settings-row-sub">{subtitle}</p>}
        </div>
      </div>
      <div className="cinbox-settings-section-body">{children}</div>
    </section>
  );
}

// A WYSIWYG contentEditable box only turns pasted content into formatting
// when the clipboard carries real HTML data — pasted HTML *source* (the raw
// tags as text) just lands as literal text. This toggle lets you paste/edit
// raw HTML directly instead, stored verbatim, no interpretation needed.
function HtmlEditorWithSourceToggle({ value, onChange, toolbar, placeholder, editorClassName }) {
  const [sourceMode, setSourceMode] = useState(false);

  return (
    <div>
      <div className="cinbox-source-toggle-row">
        <button
          type="button"
          className="cinbox-composer-link-btn"
          onClick={() => setSourceMode((v) => !v)}
        >
          {sourceMode ? <><EditIcon fontSize="inherit" /> Edit visually</> : <><CodeIcon fontSize="inherit" /> Edit as HTML</>}
        </button>
      </div>
      {sourceMode ? (
        <textarea
          className="cinbox-html-source"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste raw HTML here…"
          rows={8}
        />
      ) : (
        <Editor
          value={value}
          onChange={(e) => onChange(e.target.value)}
          containerProps={{ className: editorClassName }}
          placeholder={placeholder}
        >
          {toolbar}
        </Editor>
      )}
    </div>
  );
}

export default function SettingsPanel({
  connection,
  onConnect,
  onDisconnect,
  onSignatureChange,
  labels,
  labelsLoading,
  onSyncLabelsChange,
  templates,
  onSaveTemplate,
  onDeleteTemplate,
  companySignatureHtml,
  onClose,
}) {
  const [connecting, setConnecting] = useState(false);
  const [signatureDraft, setSignatureDraft] = useState(connection.signatureHtml || "");
  const [signatureSaved, setSignatureSaved] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateBody, setNewTemplateBody] = useState("");

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await onConnect(); // navigates the browser to Google's consent screen on success
    } finally {
      setConnecting(false);
    }
  };

  const handleSaveSignature = () => {
    onSignatureChange(signatureDraft);
    setSignatureSaved(true);
    setTimeout(() => setSignatureSaved(false), 2000);
  };

  const handleUseCompanyTemplate = () => {
    setSignatureDraft(companySignatureHtml || "");
  };

  const handleAddTemplate = () => {
    if (!newTemplateName.trim() || !newTemplateBody.replace(/<[^>]+>/g, "").trim()) return;
    onSaveTemplate(newTemplateName.trim(), newTemplateBody);
    setNewTemplateName("");
    setNewTemplateBody("");
  };

  return (
    <div className="cinbox-settings-overlay" onClick={onClose}>
      <div className="cinbox-settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="cinbox-settings-header">
          <span className="stripe-section-title">My Inbox Settings</span>
          <button className="cinbox-icon-btn" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="cinbox-settings-body">
          <SettingsSection
            icon={<MailOutlineIcon />}
            title="Gmail connection"
            subtitle={
              connection.status === "connected" ? (
                <span className="cinbox-connected-text"><CheckCircleIcon fontSize="inherit" /> Connected as {connection.connectedEmail}</span>
              ) : (
                "Not connected — connect your Gmail to start capturing replies here."
              )
            }
          >
            <div className="cinbox-settings-connect-row">
              {connection.status === "connected" ? (
                <button className="stripe-btn-clear" onClick={onDisconnect}>
                  <LinkOffIcon fontSize="small" /> Disconnect
                </button>
              ) : (
                <button className="stripe-btn-apply" onClick={handleConnect} disabled={connecting}>
                  {connecting ? "Connecting…" : "Connect Gmail"}
                </button>
              )}
              {connection.status === "connected" && connection.lastSyncedAt && (
                <span className="cinbox-settings-hint">Last synced {new Date(connection.lastSyncedAt).toLocaleString()}</span>
              )}
            </div>
            {connection.status === "error" && (
              <div className="cinbox-settings-note">
                <ErrorOutlineIcon fontSize="inherit" /> Gmail connection lost{connection.lastSyncError ? `: ${connection.lastSyncError}` : ""} — reconnect above to resume capturing replies.
              </div>
            )}
          </SettingsSection>

          <SettingsSection
            icon={<SyncOutlinedIcon />}
            title="Sync labels"
            subtitle="Only mail under the checked Gmail labels is pulled into My Inbox."
          >
            {connection.status !== "connected" ? (
              <p className="cinbox-settings-hint">Connect Gmail first to choose labels.</p>
            ) : labelsLoading ? (
              <p className="cinbox-settings-hint">Loading labels…</p>
            ) : labels?.length > 0 ? (
              <div className="cinbox-label-list">
                {labels.map((l) => {
                  const checked = (connection.syncLabelIds || []).includes(l.id);
                  return (
                    <label key={l.id} className="cinbox-label-item">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const current = connection.syncLabelIds || [];
                          const next = checked ? current.filter((id) => id !== l.id) : [...current, l.id];
                          onSyncLabelsChange(next);
                        }}
                      />
                      <span>{l.name}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="cinbox-settings-hint">No labels found on this mailbox.</p>
            )}
            <p className="cinbox-settings-hint">
              {(connection.syncLabelIds || []).length > 0
                ? "Only mail carrying at least one checked label is captured — everything else is skipped."
                : "No labels selected — every incoming reply is captured, with no label restriction."}
            </p>
          </SettingsSection>

          <SettingsSection
            icon={<DriveFileRenameOutlineIcon />}
            title="Reply signature"
            subtitle="Automatically appended to the end of every outbound reply."
          >
            <div className="cinbox-settings-connect-row">
              <button className="stripe-btn-clear" onClick={handleUseCompanyTemplate} disabled={!companySignatureHtml}>
                <BusinessOutlinedIcon fontSize="small" /> Use company template
              </button>
            </div>
            <HtmlEditorWithSourceToggle
              value={signatureDraft}
              onChange={setSignatureDraft}
              editorClassName="cinbox-rsw-container cinbox-signature-editor"
              placeholder="e.g. Best regards, Zefyron Team"
              toolbar={
                <Toolbar>
                  <BtnBold />
                  <BtnItalic />
                  <BtnUnderline />
                  <BtnLink />
                </Toolbar>
              }
            />
            <button className="stripe-btn-apply cinbox-save-btn" onClick={handleSaveSignature}>
              {signatureSaved ? "Saved" : "Save signature"}
            </button>
          </SettingsSection>

          <SettingsSection
            icon={<ArticleOutlinedIcon />}
            title="Reply templates"
            subtitle="Canned responses you can insert into the reply composer."
          >
            {templates?.length > 0 && (
              <div className="cinbox-template-list">
                {templates.map((t) => (
                  <div key={t._id} className="cinbox-template-list-item">
                    <span>{t.name}</span>
                    <button className="cinbox-icon-btn" onClick={() => onDeleteTemplate(t._id)} title="Delete template">
                      <DeleteOutlineIcon fontSize="small" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              type="text"
              className="cinbox-template-name-input"
              placeholder="Template name (e.g. Follow-up)"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
            />
            <HtmlEditorWithSourceToggle
              value={newTemplateBody}
              onChange={setNewTemplateBody}
              editorClassName="cinbox-rsw-container cinbox-signature-editor"
              placeholder="Template content…"
              toolbar={
                <Toolbar>
                  <BtnBold />
                  <BtnItalic />
                  <BtnUnderline />
                  <BtnLink />
                </Toolbar>
              }
            />
            <button className="stripe-btn-apply cinbox-save-btn" onClick={handleAddTemplate}>
              Add template
            </button>
          </SettingsSection>
        </div>
      </div>
    </div>
  );
}
